import { generalAppVariables } from '@/constants';
import http, { HTTPResultAsync } from '@/utils/http';
import { getIdempotencyKey } from '@/utils/idempotency';
import useSwrvState from '@/utils/useSwrvState';
import useSWRV from 'swrv';
import { IBackup } from './IBackup';
import { IDeployment } from './IDeployment';
import { CreateDeploymentRequest } from './dto/CreateDeployment';
import { DraftRequest } from './dto/Draft';
import { GetAccessTokenRequest } from './dto/GetAccessToken';
import { GetByCompanyRequest } from './dto/GetByCompany';
import { GetConfigurablesRequest } from './dto/GetConfigurables';
import { GetCurrentPriceRequest } from './dto/GetCurrentPrice';
import { GetDeploymentRequest } from './dto/GetDeployment';
import { GetDeploymentBackupsRequest } from './dto/GetDeploymentBackups';
import { GetDeploymentsRequest } from './dto/GetDeployments';
import { RestoreDeploymentRequest } from './dto/RestoreDeployment';
import { UnlockUserRequest } from './dto/UnlockUser';
import { UpdateDeploymentRequest } from './dto/UpdateDeployment';
import { UpdateDomainRequest } from './dto/UpdateDomain';
import { CompanyID } from './dto/companyID';
import { GetPastDueDeps } from './dto/pastDueDep';
import { GetPreviewRequest } from './dto/GetPreview';

export async function getAccessToken(payload: GetAccessTokenRequest) {
  return http.sendSafe('GET', `/auth/countly-server?deploymentId=${payload.deploymentId}&userId=${payload.userId}`);
}

export async function getPreview(payload: GetPreviewRequest) {
  return http.sendSafe('POST', `/deployments/generate-preview`, payload);
}

export async function getReview(payload: GetPreviewRequest) {
  return http.sendSafe('POST', `/deployments/generate-review`, payload);
}

export async function getOne(payload: GetDeploymentRequest) {
  return http.sendSafe('GET', `/deployments/${payload._id}/populated`);
}

export async function getAll(payload: GetDeploymentsRequest): Promise<IDeployment[]> {
  const resp = await http.send('GET', `/deployments?company=${payload.company}&project=${payload.project}`);
  return resp.payload as IDeployment[];
}

export async function getAllSafe(payload: GetDeploymentsRequest) {
  return http.sendSafe('GET', `/deployments?company=${payload.company}&project=${payload.project}`);
}

// the by-company endpoint defaults to returning only 3 deployments; callers
// that need the full list pass this as `count` (a plain limit server-side).
// keeping one shared value also keeps their SWRV cache entries shared.
export const DEPLOYMENTS_FETCH_ALL_COUNT = 500;

export async function getByCompany(payload: GetByCompanyRequest): Promise<IDeployment[]> {
  const url = payload.count ? `/deployments/by-company/${payload.company}?count=${payload.count}` : `/deployments/by-company/${payload.company}`;
  const resp = await http.send('GET', url);
  return resp.payload as IDeployment[];
}

export async function getConfigurables(payload: GetConfigurablesRequest) {
  return http.sendSafe('GET', `/plans/configurables?providerCode=${payload.providerCode}`);
}

export async function getConfigurablesPublic(payload: GetConfigurablesRequest) {
  return http.sendSafe('GET', `/plans/configurables/public/${payload.providerCode}`);
}

export async function getEvents(_id: string) {
  return http.sendSafe('GET', `/deployments/events?_id=${_id}`);
}

export async function getDeploymentCount() {
  return http.sendSafe('GET', '/deployments/by-user');
}

export async function getCurrentPrice(payload: GetCurrentPriceRequest) {
  return http.sendSafe('POST', '/deployments/pricing', payload);
}

export async function getBackups(_id: string): Promise<IBackup> {
  const backupsResponse = await http.send('GET', `/deployments/${_id}/backups`);
  return backupsResponse.payload;
}

export async function getPastDue(payload: CompanyID): HTTPResultAsync {
  return http.sendSafe('GET', `/deployments/pastdue/${payload.company}`);
}

export async function getPastDuePromise(payload: CompanyID): Promise<GetPastDueDeps[]> {
  const resp = await http.send('GET', `/deployments/pastdue/${payload.company}`);
  return resp.payload as GetPastDueDeps[];
}

function getPastDueSwr(payload: CompanyID) {
  const {
    data,
    error,
    mutate,
    isValidating,
  } = useSWRV(
    () => payload && `/deployments/pastdue/${payload.company}`,
    () => getPastDuePromise(payload),
    {
      revalidateOnFocus: true,
      refreshInterval: generalAppVariables.REFRESH_INTERVAL,
    },
  );
  const swrState = useSwrvState(data, error, isValidating);
  return {
    data,
    error,
    mutate,
    isValidating,
    swrState,
  };
}

function getByCompanySwr(payload: GetByCompanyRequest) {
  const count = typeof payload.count !== 'undefined' ? payload.count : 3;
  const {
    data,
    error,
    mutate,
    isValidating,
  } = useSWRV(
    () => payload && `/deployments/by-company/${payload.company}?count=${count}`,
    () => getByCompany(payload),
    {
      revalidateOnFocus: true,
      refreshInterval: generalAppVariables.REFRESH_INTERVAL,
    },
  );

  const swrState = useSwrvState(data, error, isValidating);
  return {
    data,
    error,
    mutate,
    isValidating,
    swrState,
  };
}

// Swr companion of get
function getAllSwr(payload: GetDeploymentsRequest) {
  const {
    data,
    error,
    mutate,
    isValidating,
  } = useSWRV(
    () => payload && `/deployments?company=${payload.company}&project=${payload.project}`,
    () => getAll(payload),
    {
      revalidateOnFocus: true,
      refreshInterval: generalAppVariables.REFRESH_INTERVAL,
    },
  );

  const swrState = useSwrvState(data, error, isValidating);
  return {
    data,
    error,
    mutate,
    isValidating,
    swrState,
  };
}

function getBackupsSwr(payload: GetDeploymentBackupsRequest) {
  const {
    data,
    error,
    mutate,
    isValidating,
  } = useSWRV(
    () => payload && `/deployments/${payload.deployment}/backups`,
    () => getBackups(payload.deployment),
    {
      revalidateOnFocus: true,
      refreshInterval: generalAppVariables.REFRESH_INTERVAL,
    },
  );

  return {
    data,
    error,
    mutate,
    isValidating,
  };
}

export const swr = {
  getAll: getAllSwr,
  getBackups: getBackupsSwr,
  getByCompany: getByCompanySwr,
  getPastDue: getPastDueSwr,
};

export async function create(payload: CreateDeploymentRequest, provisioned: boolean, idempotencyKey: string): HTTPResultAsync {
  const key = idempotencyKey || getIdempotencyKey();
  return http.sendSafe('POST', `/deployments${provisioned ? '?addProvisionSummary=1' : ''}`, payload, { 'idempotency-key': key });
}

export async function update(_id: string, payload: UpdateDeploymentRequest, provisioned: boolean): HTTPResultAsync {
  return http.sendSafe('PUT', `/deployments/${_id}${provisioned ? '?addProvisionSummary=1' : ''}`, payload);
}

export async function remove(_id: string): HTTPResultAsync {
  return http.sendSafe('DELETE', `/deployments/${_id}`);
}

export async function restore(_id: string, payload: RestoreDeploymentRequest): HTTPResultAsync {
  return http.sendSafe('POST', `/deployments/${_id}/restore`, payload);
}

export async function cancelTermination(_id: string): HTTPResultAsync {
  return http.sendSafe('GET', `/deployments/${_id}/cancel-termination`);
}

export async function restart(_id: string): HTTPResultAsync {
  return http.sendSafe('POST', `/deployments/${_id}/restart`);
}

export async function healthCheck(_id: string): HTTPResultAsync {
  return http.sendSafe('POST', `/deployments/${_id}/health-check`);
}

export async function updateDomain(_id: string, payload: UpdateDomainRequest, removeSSL: boolean): HTTPResultAsync {
  let query = '';
  if (removeSSL) {
    query = '?removeSSL=true';
  }
  return http.sendSafe('POST', `/deployments/${_id}/update-domain${query}`, payload);
}

export async function unlockUser(_id: string, payload: UnlockUserRequest): HTTPResultAsync {
  return http.sendSafe('POST', `/deployments/${_id}/unlock-users`, payload);
}

export async function verifyDomain(_id: string): HTTPResultAsync {
  return http.sendSafe('POST', `/deployments/${_id}/verify-domain`);
}

export async function getPlans(companyId: string): HTTPResultAsync {
  return http.sendSafe('GET', `/plangroups/company/${companyId}/plans`);
}

export async function getPlansPublic(): HTTPResultAsync {
  return http.sendSafe('GET', `/plans/public`);
}

export async function getDraft() {
  return http.sendSafe('GET', '/deployments/draft');
}

export async function createDraft(payload: DraftRequest) {
  const resp = await http.sendSafe('POST', '/deployments/draft', payload);
  return resp;
}

export async function updateDraft(_id: string, payload: DraftRequest) {
  const resp = await http.sendSafe('POST', `/deployments/draft/${_id}`, payload);
  return resp;
}

export async function deleteDraft(_id: string) {
  const resp = await http.sendSafe('DELETE', `/deployments/draft/${_id}`);
  return resp;
}
